/**
 * Member Care Workbench — Dipali doctrine 02-May-2026
 *
 * AI generates → human governs → Zoho executes.
 *
 * The MCE engine drops drafts into pending_review at 03:00 IST every
 * morning. This page is where Dipali / Nisha walk through them at
 * 09:00, edit anything that doesn't land, push selected drafts to
 * Zoho in bulk, and skip the rest with a recorded reason.
 *
 * Routes:
 *   /member-care/workbench                  → global view, all clients
 *   /member-care/workbench/:clientId        → single client filter
 *
 * Tabs: Today · Yesterday · Earlier (read-only)
 * Filters: trigger group + LSM region
 * Bulk: select all / by trigger → push or skip together
 *
 * Design language matches the rest of ScaleBoard CI dashboard —
 * parchment + navy, copper accents, serif body for narratives.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Cake, Heart, Moon, UserPlus, MessageCircle, Star, Mail, Sparkles,
  Send, X, Edit3, Eye, ChevronDown, ChevronRight, Filter, Loader2,
  CheckCircle2, AlertCircle, Calendar, Users, Layers, RefreshCw,
  ArrowLeft, FileText,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const ICONS = {
  Cake, Heart, Moon, UserPlus, MessageCircle, Star, Mail, Sparkles,
};

// ─────────────────────────────────────────────────────────────────────────
// Greeting Autopilot strip — 09:45 IST auto-push controls + glance list
// ─────────────────────────────────────────────────────────────────────────
function AutopilotStrip({ canWrite, allClients }) {
  const [cfg, setCfg] = useState(null);
  const [sent, setSent] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [freeze, setFreeze] = useState(null);

  const load = () => {
    api.get('/member-care/workbench/autopilot').then(r => setCfg(r.data)).catch(() => {});
    api.get('/member-care/workbench/autopilot/sent-today').then(r => setSent(r.data)).catch(() => {});
    api.get('/zoho/freeze').then(r => setFreeze(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const toggleFreeze = async () => {
    const next = !freeze?.frozen;
    const sure = window.confirm(next
      ? 'FREEZE all outbound Zoho messages? Nothing will be sent to Zoho (greetings, digests, tickets) until you lift it.'
      : 'Lift the Zoho freeze? Sending to Zoho resumes immediately.');
    if (!sure) return;
    try {
      await api.post('/zoho/freeze', { frozen: next, note: next ? 'Zoho migration' : '' });
      toast.success(next ? 'Zoho freeze ON — nothing goes to Zoho' : 'Zoho freeze lifted — sending resumed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!cfg) return null;
  const off = new Set(cfg.off_clients || []);

  const setMaster = async (v) => {
    await api.post('/member-care/workbench/autopilot', { master: v });
    load();
  };
  const toggleClient = async (cid) => {
    await api.post('/member-care/workbench/autopilot', { client_id: cid, enabled: off.has(cid) });
    load();
  };
  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post('/member-care/workbench/autopilot/run-now', null, { timeout: 180000 });
      const lr = r.data?.last_run;
      toast.success(lr ? `Autopilot pushed ${lr.pushed} greeting${lr.pushed === 1 ? '' : 's'} to Zoho` : 'Autopilot run complete');
      load();
    } catch { toast.error('Autopilot run failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-5 py-2.5" data-testid="autopilot-strip"
         style={{ background: freeze?.frozen ? '#FBEAEA' : cfg.master ? '#F0F7F2' : '#FDF3F3', borderBottom: '1px solid #E5E1DC' }}>
      {freeze?.frozen && (
        <div className="flex flex-wrap items-center gap-2 mb-1.5 rounded-lg px-3 py-2"
             style={{ background: '#A32D2D' }} data-testid="zoho-freeze-banner">
          <span className="text-[12px] font-bold text-white">
            ⛔ ZOHO FREEZE ON — nothing is being sent to Zoho (migration in progress)
          </span>
          <span className="text-[10.5px] text-[#F3C6C6]">
            since {freeze.since ? new Date(freeze.since).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''} · greetings stay safely queued
          </span>
          <button onClick={toggleFreeze} data-testid="zoho-freeze-lift"
            className="ml-auto text-[11px] px-3 py-1 rounded-full font-bold bg-white" style={{ color: '#A32D2D' }}>
            Lift freeze — resume sending
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Send size={13} style={{ color: cfg.master ? '#2D8B5E' : '#A32D2D' }} />
        <span className="text-[12px] font-semibold" style={{ color: '#1B2A4A' }}>
          Greeting Autopilot
        </span>
        <span className="text-[10.5px]" style={{ color: '#6B7280' }}>
          pushes today's birthday · anniversary · spouse/kids · festival greetings to Zoho at 09:45 IST
        </span>
        {canWrite && (
          <button onClick={() => setMaster(!cfg.master)} data-testid="autopilot-master-toggle"
            className="text-[10.5px] px-2.5 py-0.5 rounded-full font-bold"
            style={{ background: cfg.master ? '#2D8B5E' : '#A32D2D', color: '#fff' }}>
            {cfg.master ? 'ON' : 'OFF — nothing auto-sends'}
          </button>
        )}
        {canWrite && cfg.master && !freeze?.frozen && (
          <button onClick={runNow} disabled={busy} data-testid="autopilot-run-now"
            className="text-[10.5px] px-2 py-0.5 rounded border border-[#C9DBCF] hover:bg-white disabled:opacity-50 inline-flex items-center gap-1"
            style={{ color: '#1f5736' }}>
            {busy ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
            {busy ? 'Pushing…' : 'Run now'}
          </button>
        )}
        {canWrite && !freeze?.frozen && (
          <button onClick={toggleFreeze} data-testid="zoho-freeze-toggle"
            className="text-[10.5px] px-2 py-0.5 rounded border border-[#E8C9C9] hover:bg-white inline-flex items-center gap-1"
            style={{ color: '#A32D2D' }}
            title="Emergency: stop ALL outbound Zoho messages (use during Zoho migrations)">
            ⛔ Freeze Zoho sends
          </button>
        )}
        <button onClick={() => setOpen(!open)} data-testid="autopilot-sent-today-toggle"
          className="ml-auto text-[11px] font-semibold inline-flex items-center gap-1 hover:underline"
          style={{ color: '#1B2A4A' }}>
          Sent by autopilot today: {sent?.count ?? 0}
          <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
        </button>
      </div>
      {cfg.master && allClients.length > 0 && canWrite && (
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          <span className="text-[9.5px] uppercase tracking-wider" style={{ color: '#8A7B5C' }}>Programmes:</span>
          {allClients.map(cid => (
            <button key={cid} onClick={() => toggleClient(cid)} data-testid={`autopilot-client-${cid}`}
              title={off.has(cid) ? 'Autopilot OFF for this programme — click to enable' : 'Autopilot ON — click to disable'}
              className="text-[10px] px-2 py-0.5 rounded-full font-semibold border"
              style={off.has(cid)
                ? { background: '#fff', color: '#9CA3AF', borderColor: '#D8D4CC', textDecoration: 'line-through' }
                : { background: '#DCEDE2', color: '#1f5736', borderColor: '#B8D8C2' }}>
              {cid.toUpperCase()}
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-2 space-y-1" data-testid="autopilot-sent-today-list">
          {!sent?.items?.length ? (
            <p className="text-[11px]" style={{ color: '#9CA3AF' }}>Nothing auto-pushed yet today.</p>
          ) : sent.items.map(d => (
            <div key={d.draft_id} className="flex flex-wrap items-center gap-2 text-[11px] bg-white rounded px-2.5 py-1.5 border" style={{ borderColor: '#E5E1DC' }}>
              <span className="font-semibold px-1.5 rounded text-[9.5px]" style={{ background: '#EEF2F8', color: '#1B2A4A' }}>
                {(d.client_id || '').toUpperCase()}
              </span>
              <span className="font-medium" style={{ color: '#1B2A4A' }}>{d.member_name}</span>
              <span style={{ color: '#6B7280' }}>{(d.trigger || '').replace(/_/g, ' ')}</span>
              <span className="ml-auto" style={{ color: '#9CA3AF' }}>
                {d.zoho_ticket_number ? `#${d.zoho_ticket_number} · ` : ''}
                {d.pushed_at ? new Date(d.pushed_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                {d.pushed_by === 'safety_net_6pm' ? ' · 6pm net' : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Skip-reason modal
// ─────────────────────────────────────────────────────────────────────────
function SkipModal({ count, reasons, onConfirm, onCancel }) {
  const [reason, setReason] = useState(reasons?.[0] || 'Not relevant');
  const [note, setNote] = useState('');
  const otherSelected = reason === 'Other';
  const valid = reason && (!otherSelected || note.trim().length > 2);
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4"
         data-testid="workbench-skip-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-5 space-y-4"
           style={{ border: '1px solid #E5E1DC' }}>
        <div className="space-y-1">
          <h3 className="text-[15px] font-medium" style={{ color: '#1B2A4A' }}>
            Skip {count} {count === 1 ? 'draft' : 'drafts'}?
          </h3>
          <p className="text-[12px]" style={{ color: '#6B7280' }}>
            Skipped drafts won't be auto-pushed by the 6pm safety net. Pick a
            reason — this becomes part of the audit trail.
          </p>
        </div>
        <div className="space-y-1.5">
          <label className="text-[10.5px] uppercase tracking-wider font-medium"
                 style={{ color: '#6B7280' }}>Reason</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="workbench-skip-reason"
            className="w-full text-[13px] px-2.5 py-1.5 rounded border bg-white"
            style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}>
            {reasons.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {otherSelected && (
          <div className="space-y-1.5">
            <label className="text-[10.5px] uppercase tracking-wider font-medium"
                   style={{ color: '#6B7280' }}>Note (required for Other)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoFocus
              data-testid="workbench-skip-note"
              className="w-full text-[13px] px-2.5 py-1.5 rounded border bg-white"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
              placeholder='Why are we skipping these drafts today?'
            />
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className="text-[12px] px-3 py-1.5 rounded hover:bg-gray-50"
            style={{ color: '#6B7280' }}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason, note)}
            disabled={!valid}
            data-testid="workbench-skip-confirm"
            className="text-[12px] px-3 py-1.5 rounded text-white disabled:opacity-50 hover:opacity-90"
            style={{ background: '#1B2A4A' }}>
            Skip {count}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Bulk-push confirmation modal
// ─────────────────────────────────────────────────────────────────────────
function BulkPushConfirm({ breakdown, total, onConfirm, onCancel, busy }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4"
         data-testid="workbench-bulk-confirm">
      <div className="bg-white rounded-lg max-w-md w-full p-5 space-y-4"
           style={{ border: '1px solid #E5E1DC' }}>
        <div className="space-y-1">
          <h3 className="text-[15px] font-medium" style={{ color: '#1B2A4A' }}>
            Push {total} {total === 1 ? 'draft' : 'drafts'} to Zoho?
          </h3>
          <p className="text-[12px]" style={{ color: '#6B7280' }}>
            Each draft becomes a Zoho Desk ticket. The LSM completes the
            outreach from Zoho. This action cannot be undone.
          </p>
        </div>
        <div className="rounded p-3 space-y-1 text-[12px]"
             style={{ background: '#F7F5F2', color: '#1B2A4A' }}>
          {breakdown.map(b => (
            <div key={b.label} className="flex items-center justify-between">
              <span>{b.label}</span>
              <strong>{b.count}</strong>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1 border-t mt-1.5"
               style={{ borderColor: '#E5E1DC' }}>
            <span className="font-medium">Total</span>
            <strong>{total}</strong>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-[12px] px-3 py-1.5 rounded hover:bg-gray-50 disabled:opacity-50"
            style={{ color: '#6B7280' }}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="workbench-bulk-confirm-btn"
            className="text-[12px] px-3 py-1.5 rounded text-white disabled:opacity-60 hover:opacity-90 inline-flex items-center gap-1"
            style={{ background: '#1B2A4A' }}>
            {busy
              ? <><Loader2 size={12} className="animate-spin" /> Pushing…</>
              : <><Send size={12} /> Push {total}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Per-draft preview drawer (full email view)
// ─────────────────────────────────────────────────────────────────────────
function DraftPreviewDrawer({ draft, onClose, onSave, onReset, onRegenerate }) {
  const [subject, setSubject] = useState(draft?.subject || '');
  const [body, setBody] = useState(draft?.body || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  useEffect(() => {
    setSubject(draft?.subject || '');
    setBody(draft?.body || '');
    setEditing(false);
  }, [draft?.draft_id]);

  if (!draft) return null;
  const dirty = subject !== draft.subject || body !== draft.body;
  const readonly = draft.status !== 'pending_review';
  // Reset is available only when an original snapshot exists AND the
  // current text differs from it (otherwise the button would be a
  // no-op and visually confusing).
  const hasOriginal = !!(draft.original_subject || draft.original_body);
  const differsFromOriginal = hasOriginal && (
    (draft.subject || '') !== (draft.original_subject || '') ||
    (draft.body || '') !== (draft.original_body || '')
  );

  const handleSave = async () => {
    if (!dirty) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave({ subject, body });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!hasOriginal) {
      window.alert("This draft was created before reset snapshots existed. " +
                    "New drafts from tomorrow's cron onwards will support reset.");
      return;
    }
    if (!window.confirm(
      "Revert this draft to Claude's original text? Your edits will be lost.")) return;
    setResetting(true);
    try {
      await onReset();
      setEditing(false);
    } finally {
      setResetting(false);
    }
  };

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    if (body && body.trim().length > 0
        && !window.confirm(
          "Re-run Claude on this draft? The current body will be replaced.")) return;
    setRegenerating(true);
    try {
      await onRegenerate();
      setEditing(false);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-black/30"
         onClick={onClose} data-testid="workbench-drawer">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl h-full overflow-y-auto"
        style={{ background: '#FFFEFB', borderLeft: '1px solid #E5E1DC' }}>
        {/* Header */}
        <div className="px-5 py-3 flex items-center justify-between sticky top-0 z-10"
             style={{ background: '#1B2A4A', color: '#FFFFFF' }}>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
                  style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
              {readonly ? draft.status : 'Pending review'}
            </span>
            <span className="text-[13px] font-medium">{draft.member_name}</span>
            <span className="text-[11px] opacity-70">
              · {draft.member_tier} · {draft.member_city}
            </span>
          </div>
          <button onClick={onClose} className="hover:opacity-80">
            <X size={18} />
          </button>
        </div>
        {/* Member context */}
        <div className="px-5 py-3 space-y-2"
             style={{ background: '#F7F5F2', borderBottom: '1px solid #E5E1DC' }}>
          <div className="flex flex-wrap gap-1.5">
            {(draft.soul_chips || []).map((c, i) => (
              <span key={i}
                    className="text-[10.5px] px-2 py-0.5 rounded bg-white"
                    style={{ color: '#1B2A4A', border: '1px solid #E5E1DC' }}>
                {c}
              </span>
            ))}
            {draft.lsm && (
              <span className="text-[10.5px] px-2 py-0.5 rounded ml-auto"
                    style={{ background: '#1B2A4A', color: '#FFFFFF' }}>
                LSM: {draft.lsm}
              </span>
            )}
          </div>
          {(draft.talking_points || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {draft.talking_points.map((p, i) => (
                <span key={i}
                      className="text-[10.5px] px-2 py-0.5 rounded"
                      style={{ background: '#FFF8EE', color: '#8A4B00' }}>
                  {p}
                </span>
              ))}
            </div>
          )}
          {draft.last_interaction && (
            <div className="text-[10.5px]" style={{ color: '#6B7280' }}>
              Last interaction: {draft.last_interaction.slice(0, 10)}
              {draft.days_since_last && ` · ${draft.days_since_last} days ago`}
            </div>
          )}
          {draft.zoho_ticket_url && (
            <a href={draft.zoho_ticket_url} target="_blank" rel="noopener noreferrer"
               className="text-[10.5px] underline"
               style={{ color: '#1B2A4A' }}>
              View Zoho ticket →
            </a>
          )}
        </div>
        {/* Subject + body */}
        <div className="px-5 py-4 space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[10.5px] uppercase tracking-wider font-medium"
                     style={{ color: '#6B7280' }}>Subject</label>
              {!readonly && !editing && (
                <button onClick={() => setEditing(true)}
                        data-testid="workbench-edit-toggle"
                        className="text-[10.5px] inline-flex items-center gap-1"
                        style={{ color: '#1B2A4A' }}>
                  <Edit3 size={10} /> Edit
                </button>
              )}
            </div>
            {editing ? (
              <input value={subject}
                     onChange={(e) => setSubject(e.target.value)}
                     className="w-full text-[14px] px-2.5 py-1.5 rounded border bg-white"
                     style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                     data-testid="workbench-edit-subject" />
            ) : (
              <div className="text-[14px] font-medium" style={{ color: '#1B2A4A' }}>
                {subject || <em style={{ color: '#9CA3AF' }}>No subject</em>}
              </div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-[10.5px] uppercase tracking-wider font-medium"
                   style={{ color: '#6B7280' }}>Body</label>
            {editing ? (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={Math.max(8, (body || '').split('\n').length + 2)}
                data-testid="workbench-edit-body"
                className="w-full text-[13px] px-3 py-2 rounded border bg-white"
                style={{
                  borderColor: '#E5E1DC', color: '#1B2A4A',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  lineHeight: 1.65,
                }} />
            ) : (
              <div className="text-[13px] whitespace-pre-wrap"
                   style={{
                     color: '#1B2A4A',
                     fontFamily: 'Georgia, "Times New Roman", serif',
                     lineHeight: 1.65,
                   }}>
                {body || <em style={{ color: '#9CA3AF' }}>No body</em>}
              </div>
            )}
            {!readonly && !editing && !body && onRegenerate && (
              <div className="flex items-center justify-between text-[10.5px] mt-2 px-2.5 py-2 rounded"
                   style={{ background: '#FFF8EE', color: '#8A4B00',
                             border: '1px solid #F4D8A8' }}>
                <span>
                  This draft has no body — likely from a legacy run before
                  the body-mapping fix. Re-run Claude to fill it in.
                </span>
                <button onClick={handleRegenerate} disabled={regenerating}
                        data-testid="workbench-regenerate-empty"
                        className="text-[10.5px] px-2.5 py-1 rounded inline-flex items-center gap-1 hover:bg-white disabled:opacity-60"
                        style={{ color: '#8A4B00',
                                  border: '1px solid #F4D8A8',
                                  background: 'white' }}>
                  {regenerating
                    ? <><Loader2 size={10} className="animate-spin" /> Drafting…</>
                    : <><Sparkles size={10} /> Regenerate</>}
                </button>
              </div>
            )}
            {/* CEO recovery: empty body on a pushed/skipped draft —
                regenerate so the LSM can paste the new content into
                the live Zoho ticket. */}
            {readonly && !body && onRegenerate && (
              <div className="flex items-center justify-between text-[10.5px] mt-2 px-2.5 py-2 rounded"
                   style={{ background: '#FEF2F2', color: '#7F1D1D',
                             border: '1px solid #FCA5A5' }}>
                <span>
                  An empty draft was pushed to Zoho. Click regenerate to
                  produce content you can copy-paste into ticket{' '}
                  <strong>{draft.zoho_ticket_number || '(see below)'}</strong>.
                </span>
                <button onClick={handleRegenerate} disabled={regenerating}
                        data-testid="workbench-regenerate-pushed-empty"
                        className="text-[10.5px] px-2.5 py-1 rounded inline-flex items-center gap-1 hover:bg-white disabled:opacity-60"
                        style={{ color: '#7F1D1D',
                                  border: '1px solid #FCA5A5',
                                  background: 'white' }}>
                  {regenerating
                    ? <><Loader2 size={10} className="animate-spin" /> Drafting…</>
                    : <><Sparkles size={10} /> Regenerate (CEO override)</>}
                </button>
              </div>
            )}
            {!readonly && !editing && body && onRegenerate && (
              <div className="mt-1.5">
                <button onClick={handleRegenerate} disabled={regenerating}
                        data-testid="workbench-regenerate"
                        title="Re-run Claude — useful if the current draft does not land"
                        className="text-[10.5px] inline-flex items-center gap-1 hover:underline disabled:opacity-60"
                        style={{ color: '#6B7280' }}>
                  {regenerating
                    ? <><Loader2 size={10} className="animate-spin" /> Drafting fresh take…</>
                    : <><Sparkles size={10} /> Regenerate with Claude</>}
                </button>
              </div>
            )}
          </div>
          {editing && (
            <div className="flex items-center gap-2">
              <button onClick={handleSave} disabled={saving || !dirty}
                      data-testid="workbench-edit-save"
                      className="text-[12px] px-3 py-1.5 rounded text-white disabled:opacity-50 hover:opacity-90 inline-flex items-center gap-1"
                      style={{ background: '#1B2A4A' }}>
                {saving
                  ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
                  : <><CheckCircle2 size={12} /> Save edit</>}
              </button>
              <button onClick={() => {
                setSubject(draft.subject || '');
                setBody(draft.body || '');
                setEditing(false);
              }} className="text-[12px] px-3 py-1.5 rounded hover:bg-gray-50"
                 style={{ color: '#6B7280' }}>
                Cancel
              </button>
              {differsFromOriginal && (
                <button onClick={handleReset} disabled={resetting}
                        data-testid="workbench-reset"
                        title="Revert to Claude's original draft (before any manual edits)"
                        className="text-[12px] px-3 py-1.5 rounded ml-auto inline-flex items-center gap-1 hover:bg-gray-50 disabled:opacity-60"
                        style={{ color: '#8A4B00',
                                  border: '1px solid #F4D8A8',
                                  background: '#FFF8EE' }}>
                  {resetting
                    ? <><Loader2 size={12} className="animate-spin" /> Resetting…</>
                    : <><RefreshCw size={12} /> Reset to Claude's original</>}
                </button>
              )}
            </div>
          )}
          {!editing && draft.edited && hasOriginal && !readonly && (
            <div className="flex items-center justify-between text-[10.5px] mt-1 px-2 py-1.5 rounded"
                 style={{ background: '#FFF8EE', color: '#8A4B00',
                           border: '1px solid #F4D8A8' }}>
              <span>This draft has been manually edited.</span>
              <button onClick={handleReset} disabled={resetting}
                      data-testid="workbench-reset-inline"
                      className="text-[10.5px] px-2 py-0.5 rounded hover:bg-white inline-flex items-center gap-1 disabled:opacity-60"
                      style={{ color: '#8A4B00',
                                border: '1px solid #F4D8A8',
                                background: 'white' }}>
                {resetting
                  ? <><Loader2 size={10} className="animate-spin" /> Resetting…</>
                  : <><RefreshCw size={10} /> Reset to original</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main Workbench page
// ─────────────────────────────────────────────────────────────────────────
export default function MemberCareWorkbench() {
  const { clientId: routeClientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCeo = (user?.role || '').toLowerCase() === 'ceo';
  // 4-Jun-2026 — Dipali reported Tanisha / Shiva / Nisha couldn't push
  // to Zoho. Backend `_assert_workbench_access` already allows the
  // Concierge® LSM team (cc-* divisions, leader / sub_leader). The
  // frontend was still gating every write button behind `isCeo` alone,
  // so the buttons never rendered. Mirror the backend rule here.
  const role = (user?.role || '').toLowerCase();
  const division = (user?.division_id || '').toLowerCase();
  const isCcLsm = ['leader', 'sub_leader', 'sub-leader'].includes(role)
                  && division.startsWith('cc-');
  const canWrite = isCeo || isCcLsm;

  const [clientFilter, setClientFilter] = useState(routeClientId || null);
  const [lsmFilter, setLsmFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all'); // birthday|dormant|soul|all
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [drawerDraft, setDrawerDraft] = useState(null);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [view, setView] = useState('today'); // today | history
  const [historyDate, setHistoryDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  // Manual draft generator (CEO only) — runs the MCE sweep for one
  // client right now instead of waiting for tonight's 3am cron. Lets
  // Dipali test the Workbench end-to-end on demand.
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState(null);

  // Self-healing banner — detects legacy drafts where body is empty
  // (pre-2026-02 bug where Claude's "email" block was dropped at
  // insert time). Shows a one-click heal button when work remains.
  const [healInfo, setHealInfo] = useState(null);
  const [healBusy, setHealBusy] = useState(false);

  // Per-client "draftable" list — drives the client filter chips and
  // the "Generate today's drafts" dropdown. Loaded once on mount.
  const [allClients, setAllClients] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/member-care/config');
        if (cancelled) return;
        const list = (r.data?.client_ids || r.data?.clients || []).map(c =>
          typeof c === 'string' ? c : (c.client_id || c.id || c.name)
        ).filter(Boolean);
        setAllClients(list);
      } catch {
        // Endpoint optional — fall back to clients with drafts today
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const downloadCsv = () => {
    const url = new URL(`${window.location.origin}/api/member-care/workbench/export.csv`);
    if (clientFilter) url.searchParams.set('client_id', clientFilter);
    if (lsmFilter && lsmFilter !== 'all') url.searchParams.set('lsm', lsmFilter);
    if (view === 'history' && historyDate) url.searchParams.set('date', historyDate);
    // The CSV endpoint needs the bearer token but Response can't carry
    // headers via window.open — easiest is fetch + blob download.
    const token = localStorage.getItem('access_token')
                   || localStorage.getItem('token') || '';
    fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      if (!r.ok) {
        const text = await r.text();
        throw new Error(text || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      const date = view === 'today' ? new Date().toISOString().slice(0, 10) : historyDate;
      a.download = `member_care_workbench_${date}` +
        `${clientFilter ? `_${clientFilter}` : ''}` +
        `${lsmFilter && lsmFilter !== 'all' ? `_${lsmFilter}` : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
    }).catch((e) => {
      window.alert(`CSV download failed: ${e.message}`);
    });
  };

  const generateToday = async (cid) => {
    if (!cid) {
      window.alert('Pick a client first');
      return;
    }
    if (!window.confirm(
      `Run the MCE sweep for ${cid.toUpperCase()} now?\n\n` +
      `This will draft today's outreach for every eligible member ` +
      `(birthdays, anniversaries, dormant, soul follow-ups, etc.) ` +
      `and store them as pending_review in the Workbench. Nothing is ` +
      `pushed to Zoho automatically.\n\n` +
      `Takes 30-90 seconds.`
    )) return;
    setGenBusy(true);
    setGenResult(null);
    try {
      const r = await api.post(
        `/member-care/${cid}/run-now`, null,
        { timeout: 30000 },
      );
      setGenResult({ ok: true, message: r.data?.message ||
        `Started sweep for ${cid}. Drafts will appear here in ~60s.`,
        client: cid });
      // Poll for completion: just refresh every 8s until we see new
      // drafts pop in. Cap at 2 minutes.
      let polls = 0;
      const poller = setInterval(async () => {
        polls += 1;
        await load();
        if (polls >= 15) clearInterval(poller);
      }, 8000);
    } catch (e) {
      setGenResult({ ok: false,
        error: e?.response?.data?.detail || e.message });
    } finally {
      setGenBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = view === 'today'
        ? `/member-care/workbench/today`
        : `/member-care/workbench/history`;
      const params = view === 'today'
        ? { client_id: clientFilter || undefined,
            lsm: lsmFilter === 'all' ? undefined : lsmFilter }
        : { date: historyDate, client_id: clientFilter || undefined };
      const r = await api.get(url, { params });
      setData(r.data);
      // Drop selections that are no longer relevant
      setSelected(new Set());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[workbench load]', e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clientFilter, lsmFilter, view, historyDate]);

  useEffect(() => { load(); }, [load]);

  // Probe for legacy empty-body drafts on the same client/view —
  // CEO only. Quietly fires a dry-run; banner only shows if work
  // remains. Re-runs whenever the load result changes.
  useEffect(() => {
    if (!isCeo || view !== 'today') { setHealInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post(
          `/member-care/workbench/_admin/heal-empty-bodies`,
          null,
          { params: { dry_run: true,
                      client_id: clientFilter || undefined } },
        );
        if (cancelled) return;
        const count = r.data?.matched_total || 0;
        setHealInfo(count > 0 ? r.data : null);
      } catch {
        if (!cancelled) setHealInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isCeo, view, clientFilter, data?.total]);

  const healEmptyBodies = async () => {
    if (!healInfo) return;
    const n = healInfo.matched_total || 0;
    if (!window.confirm(
      `Re-run Claude on ${n} legacy draft${n === 1 ? '' : 's'} ` +
      `with empty body${n === 1 ? '' : 's'}? ` +
      `This processes up to 200 at a time.\n\nTakes ~${Math.ceil(n / 3 * 6)}s.`)) return;
    setHealBusy(true);
    try {
      const r = await api.post(
        `/member-care/workbench/_admin/heal-empty-bodies`,
        null,
        { params: { dry_run: false,
                    client_id: clientFilter || undefined,
                    limit: 200 },
          timeout: 240000 },
      );
      const healed = r.data?.healed || 0;
      const failed = r.data?.failed || 0;
      const remaining = r.data?.remaining || 0;
      window.alert(
        `Healed ${healed} draft${healed === 1 ? '' : 's'}.` +
        (failed ? `\n${failed} failed.` : '') +
        (remaining ? `\n${remaining} remaining — click again to continue.` : '')
      );
      await load();
    } catch (e) {
      window.alert(`Heal failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setHealBusy(false);
    }
  };

  // Visible sections after group filter
  const visibleSections = useMemo(() => {
    if (!data?.by_trigger) return [];
    if (groupFilter === 'all') return data.by_trigger;
    return data.by_trigger.filter(s => s.group === groupFilter);
  }, [data, groupFilter]);

  // Selection helpers
  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllInSection = (section) => {
    const ids = section.drafts
      .filter(d => d.status === 'pending_review')
      .map(d => d.draft_id);
    setSelected(prev => new Set([...prev, ...ids]));
  };
  const deselectAllInSection = (section) => {
    const ids = new Set(section.drafts.map(d => d.draft_id));
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  };
  const selectAllPending = () => {
    const ids = visibleSections.flatMap(s =>
      s.drafts.filter(d => d.status === 'pending_review').map(d => d.draft_id),
    );
    setSelected(new Set(ids));
  };
  const clearSelection = () => setSelected(new Set());

  // Bulk action breakdown for the confirm modal
  const bulkBreakdown = useMemo(() => {
    if (!data?.by_trigger) return [];
    const counts = {};
    visibleSections.forEach(s => {
      const n = s.drafts.filter(d => selected.has(d.draft_id)).length;
      if (n > 0) counts[s.label] = n;
    });
    return Object.entries(counts).map(([label, count]) => ({ label, count }));
  }, [data, selected, visibleSections]);

  // Two-step doctrine: split the selection by prep vs greeting/legacy
  // so the bulk bar can show the right primary action.
  const selectedSplit = useMemo(() => {
    if (!data?.by_trigger) return { prep: 0, push: 0 };
    let prep = 0, push = 0;
    visibleSections.forEach(s => {
      s.drafts.forEach(d => {
        if (!selected.has(d.draft_id) || d.status !== 'pending_review') return;
        if (d.is_prep_brief) prep += 1;
        else push += 1;
      });
    });
    return { prep, push };
  }, [data, selected, visibleSections]);

  const doBulkMarkDone = async () => {
    // Mark every selected prep brief as done. Iterates one-by-one
    // since the API doesn't have a bulk mark-done endpoint (low
    // volume — at most 5 prep briefs per day on JLR).
    const prepIds = [];
    visibleSections.forEach(s => {
      s.drafts.forEach(d => {
        if (selected.has(d.draft_id) && d.is_prep_brief
            && d.status === 'pending_review') {
          prepIds.push(d.draft_id);
        }
      });
    });
    let done = 0, failed = 0;
    for (const id of prepIds) {
      try {
        await api.post(`/member-care/workbench/draft/${id}/mark-done`);
        done += 1;
      } catch {
        failed += 1;
      }
    }
    window.alert(`Marked ${done} prep brief${done === 1 ? '' : 's'} as done` +
                 (failed ? ` · ${failed} failed` : ''));
    clearSelection();
    load();
  };

  const doBulkPush = async () => {
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      const r = await api.post(`/member-care/workbench/bulk-push`,
                                  { draft_ids: ids });
      setShowBulkConfirm(false);
      const { pushed, already_pushed: ap, failed } = r.data;
      let msg = `${pushed} pushed to Zoho`;
      if (ap) msg += ` · ${ap} already pushed`;
      if (failed) msg += ` · ${failed} failed`;
      // Toast via alert for v1 (sonner is overkill for one screen)
      window.alert(msg);
      clearSelection();
      load();
    } catch (e) {
      window.alert(`Bulk push failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const doBulkSkip = async (reason, note) => {
    try {
      const ids = Array.from(selected);
      await api.post(`/member-care/workbench/bulk-skip`,
                      { draft_ids: ids, reason, note });
      setShowSkipModal(false);
      clearSelection();
      load();
    } catch (e) {
      window.alert(`Bulk skip failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const pushOne = async (draftId) => {
    try {
      await api.post(`/member-care/workbench/draft/${draftId}/push`);
      load();
      if (drawerDraft?.draft_id === draftId) setDrawerDraft(null);
    } catch (e) {
      window.alert(`Push failed: ${e?.response?.data?.detail || e.message}`);
    }
  };
  // Mark a PREP brief as done (06-Feb-2026 doctrine).
  // Prep briefs are internal — they never push to Zoho. The LSM
  // confirms they've actioned the brief (arranged the gift, booked
  // the dinner, etc.) by clicking "Mark as Done" instead of "Push".
  const markDone = async (draftId) => {
    try {
      await api.post(`/member-care/workbench/draft/${draftId}/mark-done`);
      load();
      if (drawerDraft?.draft_id === draftId) setDrawerDraft(null);
    } catch (e) {
      window.alert(`Mark as Done failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Converting PREP briefs to early greetings was removed 09-Jul-2026:
  // greetings fire automatically at 08:00 IST on the actual day.
  const skipOne = async (draftId, reason, note) => {
    try {
      await api.post(`/member-care/workbench/draft/${draftId}/skip`,
                      { reason, note });
      load();
    } catch (e) {
      window.alert(`Skip failed: ${e?.response?.data?.detail || e.message}`);
    }
  };
  const suggestNextExperience = async (draftId) => {
    try {
      const r = await api.post(
        `/member-care/workbench/draft/${draftId}/suggest-next-experience`,
        null, { timeout: 60000 },
      );
      // Splice the suggestion + anchor facts back into the local draft
      setData(prev => {
        if (!prev?.by_trigger) return prev;
        const sections = prev.by_trigger.map(s => ({
          ...s,
          drafts: s.drafts.map(d => d.draft_id === draftId
            ? { ...d,
                suggested_next_experience: r.data.suggestion,
                suggested_anchor_facts: r.data.anchor_facts || [] }
            : d),
        }));
        return { ...prev, by_trigger: sections };
      });
      // Also update drawer if open
      if (drawerDraft?.draft_id === draftId) {
        setDrawerDraft(prev => ({
          ...prev,
          suggested_next_experience: r.data.suggestion,
          suggested_anchor_facts: r.data.anchor_facts || [],
        }));
      }
      return r.data;
    } catch (e) {
      window.alert(`Suggestion failed: ${e?.response?.data?.detail || e.message}`);
    }
  };
  const editDraft = async ({ subject, body }) => {
    if (!drawerDraft) return;
    const r = await api.patch(
      `/member-care/workbench/draft/${drawerDraft.draft_id}/edit`,
      { subject, body },
    );
    setDrawerDraft(r.data?.draft || drawerDraft);
    load();
  };
  const resetDraft = async () => {
    if (!drawerDraft) return;
    try {
      const r = await api.post(
        `/member-care/workbench/draft/${drawerDraft.draft_id}/reset`);
      if (r.data?.ok === false) {
        window.alert(r.data?.message || 'Reset not available for this draft');
        return;
      }
      setDrawerDraft(r.data?.draft || drawerDraft);
      load();
    } catch (e) {
      window.alert(`Reset failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const regenerateDraft = async () => {
    if (!drawerDraft) return;
    // Pushed/skipped drafts need force=true (CEO override) — useful
    // when an empty body got pushed to Zoho and we need fresh
    // content to copy-paste into the live ticket.
    const isActioned = drawerDraft.status && drawerDraft.status !== 'pending_review';
    if (isActioned && !window.confirm(
      `This draft is already ${drawerDraft.status}.\n\n` +
      `Re-running Claude will refresh the body in the Workbench but ` +
      `will NOT update the Zoho ticket automatically — you'll need to ` +
      `copy-paste the new content into ticket ${drawerDraft.zoho_ticket_number || ''} manually.\n\n` +
      `Proceed?`)) return;
    try {
      const r = await api.post(
        `/member-care/workbench/draft/${drawerDraft.draft_id}/regenerate`,
        null,
        { params: isActioned ? { force: true } : {} },
      );
      setDrawerDraft(r.data?.draft || drawerDraft);
      load();
    } catch (e) {
      window.alert(
        `Regenerate failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pb-20" style={{ background: '#F7F5F2' }}
         data-testid="workbench-page">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between"
           style={{ background: '#1B2A4A', color: '#FFFFFF' }}>
        <div className="flex items-center gap-3">
          {routeClientId && (
            <button onClick={() => navigate(-1)} title="Back"
                    className="hover:opacity-80">
              <ArrowLeft size={16} />
            </button>
          )}
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
            Member Care
          </span>
          <span className="text-[15px] font-medium">Workbench</span>
          {data?.date && (
            <span className="text-[11px] opacity-70 inline-flex items-center gap-1">
              <Calendar size={11} />
              {data.date === new Date().toISOString().slice(0, 10)
                ? `Today · ${data.date}` : data.date}
              {data.read_only && ' · Read-only'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Generate today's drafts — CEO-only manual trigger.
             Without this, Dipali can't test the Workbench until the
             3am cron runs. Per her 02-May explicit request. */}
          {isCeo && view === 'today' && (
            <button
              onClick={() => generateToday(clientFilter
                || allClients[0]
                || data?.clients?.[0]?.client_id
                || 'jlr')}
              disabled={genBusy}
              data-testid="workbench-generate-now"
              title="Run the MCE sweep for the selected client now (instead of waiting for 3am cron)"
              className="text-[11px] px-3 py-1 rounded font-medium inline-flex items-center gap-1 disabled:opacity-60 hover:opacity-90"
              style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
              {genBusy
                ? <><Loader2 size={11} className="animate-spin" /> Drafting…</>
                : <><Sparkles size={11} />
                    Generate today's drafts
                    {clientFilter ? ` · ${clientFilter.toUpperCase()}` : ''}
                  </>}
            </button>
          )}
          <button onClick={downloadCsv} title="Download current view as CSV"
                   data-testid="workbench-csv-export"
                   className="text-[11px] px-2 py-1 rounded hover:bg-white/10 inline-flex items-center gap-1">
            <FileText size={11} /> CSV
          </button>
          <button onClick={load} title="Refresh"
                  data-testid="workbench-refresh"
                  className="text-[11px] px-2 py-1 rounded hover:bg-white/10 inline-flex items-center gap-1">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {/* Counters strip */}
      {data && (
        <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3"
             style={{ background: '#FFFEFB', borderBottom: '1px solid #E5E1DC' }}>
          <CounterTile label="To reach" value={data.total} icon={Users}
                        color="#1B2A4A" />
          <CounterTile label="Pending review" value={data.pending}
                        icon={AlertCircle} color="#8A4B00" />
          <CounterTile label="Sent to Zoho" value={data.sent}
                        icon={CheckCircle2} color="#2D8B5E" />
          <CounterTile label="Skipped" value={data.skipped} icon={X}
                        color="#6B7280" />
        </div>
      )}

      {/* Greeting Autopilot — 09:45 IST auto-push controls */}
      <AutopilotStrip canWrite={canWrite} allClients={allClients} />

      {/* Generate-now toast — shown briefly after the manual trigger */}
      {genResult && (
        <div className="px-5 py-2.5 text-[11px]"
             data-testid="workbench-gen-result"
             style={{
               background: genResult.ok ? '#ECFDF5' : '#FEF2F2',
               color: genResult.ok ? '#1f5736' : '#7F1D1D',
               borderBottom: `1px solid ${genResult.ok ? '#86EFAC' : '#FCA5A5'}`,
             }}>
          {genResult.ok
            ? <><CheckCircle2 size={11} className="inline mr-1.5" />
                {genResult.message} <button
                  onClick={() => setGenResult(null)}
                  className="ml-2 underline opacity-70 hover:opacity-100">
                  dismiss
                </button></>
            : <><AlertCircle size={11} className="inline mr-1.5" />
                Sweep failed: {genResult.error}</>}
        </div>
      )}

      {/* Dedupe-info banner — 4-Jun-2026 · Dipali asked us to make sure
         duplicate cards never reach the Workbench.  Engine inserts are
         now idempotent + every /today GET runs a silent dedupe sweep
         scoped to today's anchor.  When the sweep actually merged rows
         this session, surface a one-line reassurance so the team knows
         what just happened.  Auto-hides on next refresh once cleaned. */}
      {(data?.dedupe?.rows_deleted || 0) > 0 && (
        <div className="px-5 py-2 text-[12px] flex items-center gap-2"
             data-testid="workbench-dedupe-banner"
             style={{ background: '#ECF7EF', color: '#1B4A2E',
                       borderBottom: '1px solid #C8E5D2' }}>
          <CheckCircle2 size={13} />
          <span>
            <strong>{data.dedupe.rows_deleted}</strong> duplicate
            {data.dedupe.rows_deleted === 1 ? ' card' : ' cards'}
            {' '}auto-merged from {data.dedupe.clusters_merged} member
            {data.dedupe.clusters_merged === 1 ? '' : 's'} ·
            kept the most-evolved draft, deleted the rest.
          </span>
        </div>
      )}

      {/* Self-heal banner — surfaces legacy drafts whose body never
         got saved (Claude returns "email" but the older insert path
         read draft.get("body"), which was None). One-click re-runs
         Claude. CEO only; only renders when work remains. */}
      {isCeo && healInfo && (healInfo.matched_total || 0) > 0 && (
        <div className="px-5 py-3 flex items-center justify-between"
             data-testid="workbench-heal-banner"
             style={{
               background: '#FFF8EE', color: '#8A4B00',
               borderBottom: '1px solid #F4D8A8',
             }}>
          <div className="text-[12px] flex items-center gap-2">
            <AlertCircle size={13} />
            <span>
              <strong>{healInfo.matched_total}</strong> draft
              {healInfo.matched_total === 1 ? ' has' : 's have'} an
              empty body — likely from a sweep before the body-mapping
              fix. Re-run Claude to fill them in.
            </span>
          </div>
          <button onClick={healEmptyBodies} disabled={healBusy}
                  data-testid="workbench-heal-button"
                  className="text-[11.5px] px-3 py-1.5 rounded font-medium inline-flex items-center gap-1 disabled:opacity-60 hover:opacity-90"
                  style={{ background: '#8A4B00', color: 'white' }}>
            {healBusy
              ? <><Loader2 size={11} className="animate-spin" /> Drafting…</>
              : <><Sparkles size={11} /> Fix now · regenerate {Math.min(healInfo.matched_total, 200)}</>}
          </button>
        </div>
      )}

      {/* Tabs + filters */}
      <div className="px-5 py-3 flex flex-wrap items-center gap-2 sticky top-0 z-30"
           style={{ background: '#F7F5F2', borderBottom: '1px solid #E5E1DC' }}>
        {/* View tabs */}
        <div className="inline-flex rounded overflow-hidden"
             style={{ border: '1px solid #E5E1DC' }}>
          <button onClick={() => setView('today')}
                  data-testid="workbench-view-today"
                  className="text-[11px] px-3 py-1"
                  style={{
                    background: view === 'today' ? '#1B2A4A' : 'white',
                    color: view === 'today' ? 'white' : '#1B2A4A',
                  }}>
            Today
          </button>
          <button onClick={() => setView('history')}
                  data-testid="workbench-view-history"
                  className="text-[11px] px-3 py-1"
                  style={{
                    background: view === 'history' ? '#1B2A4A' : 'white',
                    color: view === 'history' ? 'white' : '#1B2A4A',
                  }}>
            Earlier days
          </button>
        </div>
        {view === 'history' && (
          <input type="date" value={historyDate}
                  onChange={(e) => setHistoryDate(e.target.value)}
                  data-testid="workbench-history-date"
                  className="text-[11px] px-2 py-1 rounded border"
                  style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                  max={new Date().toISOString().slice(0, 10)} />
        )}
        {/* Group filter — two-step doctrine (06-Feb-2026):
            Greetings (member-facing, today) and Prep (LSM-only, this
            week) are first-class peers in the queue. */}
        <div className="inline-flex rounded overflow-hidden"
             style={{ border: '1px solid #E5E1DC' }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'greeting', label: 'Greetings' },
            { id: 'prep', label: 'Prep' },
            { id: 'dormant', label: 'Dormant' },
            { id: 'soul', label: 'Soul' },
            { id: 'curated', label: 'Curated' },
          ].map(b => (
            <button key={b.id}
                    onClick={() => setGroupFilter(b.id)}
                    data-testid={`workbench-filter-${b.id}`}
                    className="text-[11px] px-2.5 py-1"
                    style={{
                      background: groupFilter === b.id ? '#E5C9A0' : 'white',
                      color: groupFilter === b.id ? '#1B2A4A' : '#6B7280',
                    }}>
              {b.label}
            </button>
          ))}
        </div>
        {/* Client chips — always visible (combines clients with
           drafts today + all configured clients in the sweep list).
           Per Dipali: when AaBee + DreamFolks onboard, even days with
           zero AaBee drafts should still show the AaBee chip so Nisha
           can switch context easily. */}
        {view === 'today' && (() => {
          const withDrafts = (data?.clients || []).map(c => c.client_id);
          const all = [...new Set([
            ...withDrafts,
            ...allClients.filter(c => !withDrafts.includes(c)),
          ])];
          return all.length === 0 ? null : (
            <div className="inline-flex flex-wrap rounded overflow-hidden"
                 data-testid="workbench-client-chips"
                 style={{ border: '1px solid #E5E1DC' }}>
              <button
                onClick={() => setClientFilter(null)}
                data-testid="workbench-client-all"
                className="text-[11px] px-2.5 py-1 font-medium"
                style={{
                  background: !clientFilter ? '#1B2A4A' : 'white',
                  color: !clientFilter ? 'white' : '#1B2A4A',
                }}>
                All
              </button>
              {all.map(cid => {
                const meta = (data?.clients || []).find(c => c.client_id === cid);
                const pending = meta?.pending || 0;
                const active = clientFilter === cid;
                return (
                  <button key={cid}
                          onClick={() => setClientFilter(cid)}
                          data-testid={`workbench-client-${cid}`}
                          className="text-[11px] px-2.5 py-1"
                          style={{
                            background: active ? '#1B2A4A' : 'white',
                            color: active ? 'white' : '#1B2A4A',
                          }}>
                    {cid.toUpperCase()}
                    {pending > 0 && (
                      <span className="ml-1 px-1 rounded text-[9px]"
                            style={{
                              background: active ? '#E5C9A0' : '#F0EEE9',
                              color: '#1B2A4A',
                            }}>
                        {pending}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })()}
        {/* LSM filter */}
        {data?.lsms && data.lsms.length > 1 && view === 'today' && (
          <select value={lsmFilter}
                   onChange={(e) => setLsmFilter(e.target.value)}
                   data-testid="workbench-lsm-filter"
                   className="text-[11px] px-2 py-1 rounded border"
                   style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}>
            <option value="all">All LSMs</option>
            {data.lsms.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
        {/* Right: select-all + counter */}
        <div className="ml-auto flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <span className="text-[11px]" style={{ color: '#1B2A4A' }}>
                {selected.size} selected
              </span>
              <button onClick={clearSelection}
                      className="text-[11px] px-2 py-1 rounded hover:bg-white"
                      style={{ color: '#6B7280' }}>
                Clear
              </button>
            </>
          )}
          {visibleSections.some(s => s.pending_count > 0) && (
            <button onClick={selectAllPending}
                    data-testid="workbench-select-all"
                    className="text-[11px] px-2 py-1 rounded border hover:bg-white"
                    style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}>
              Select all pending
            </button>
          )}
        </div>
      </div>

      {/* Main scrollable content */}
      <div className="px-5 py-4 max-w-5xl mx-auto space-y-4">
        {loading && (
          <div className="text-center py-12"
               data-testid="workbench-loading">
            <Loader2 size={24} className="animate-spin mx-auto"
                     style={{ color: '#1B2A4A' }} />
            <div className="text-[12px] mt-2" style={{ color: '#6B7280' }}>
              Loading drafts…
            </div>
          </div>
        )}
        {!loading && visibleSections.length === 0 && (
          <EmptyStateForFilter
            view={view}
            groupFilter={groupFilter}
            hasAnyData={(data?.total || 0) > 0}
            today={data?.date}
          />
        )}
        {!loading && visibleSections.map(section => (
          <SectionCard
            key={section.trigger}
            section={section}
            selected={selected}
            onToggle={toggleOne}
            onSelectAll={() => selectAllInSection(section)}
            onDeselectAll={() => deselectAllInSection(section)}
            onPreview={(d) => setDrawerDraft(d)}
            onPushOne={pushOne}
            onMarkDone={markDone}
            onSkipOne={(d) => {
              setSelected(new Set([d.draft_id]));
              setShowSkipModal(true);
            }}
            onSuggest={suggestNextExperience}
            readonly={view === 'history' || data?.read_only}
            canWrite={canWrite}
          />
        ))}
      </div>

      {/* Sticky bulk-action bar */}
      {selected.size > 0 && view === 'today' && canWrite && (
        <div className="fixed bottom-0 inset-x-0 z-40 px-4 py-2.5 border-t shadow-lg"
             style={{ background: '#1B2A4A', color: '#FFFFFF',
                       borderColor: '#0d1729' }}
             data-testid="workbench-bulk-bar">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <span className="text-[12px]">{selected.size} selected</span>
            {selectedSplit.push > 0 && (
              <button onClick={() => setShowBulkConfirm(true)}
                      data-testid="workbench-bulk-push"
                      className="text-[12px] px-3 py-1.5 rounded font-medium inline-flex items-center gap-1.5 hover:opacity-90"
                      style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
                <Send size={12} /> Push {selectedSplit.push} to Zoho
              </button>
            )}
            {selectedSplit.prep > 0 && (
              <button onClick={doBulkMarkDone}
                      data-testid="workbench-bulk-markdone"
                      className="text-[12px] px-3 py-1.5 rounded font-medium inline-flex items-center gap-1.5 hover:opacity-90"
                      title="Mark prep briefs as actioned (LSM has arranged the gift / dinner / experience)"
                      style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
                <CheckCircle2 size={12} /> Mark {selectedSplit.prep} prep as done
              </button>
            )}
            <button onClick={() => setShowSkipModal(true)}
                    data-testid="workbench-bulk-skip"
                    className="text-[12px] px-3 py-1.5 rounded inline-flex items-center gap-1.5 hover:bg-white/10"
                    style={{ border: '1px solid #3A4A6A', color: '#E5C9A0' }}>
              <X size={12} /> Skip selected with reason
            </button>
            <button onClick={clearSelection}
                    className="text-[11px] ml-auto hover:opacity-80"
                    style={{ color: '#B7C2D8' }}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Modals + drawer */}
      <DraftPreviewDrawer
        draft={drawerDraft}
        onClose={() => setDrawerDraft(null)}
        onSave={editDraft}
        onReset={resetDraft}
        onRegenerate={regenerateDraft} />
      {showSkipModal && (
        <SkipModal
          count={selected.size}
          reasons={data?.skip_reasons || ['Other']}
          onCancel={() => setShowSkipModal(false)}
          onConfirm={(reason, note) => {
            if (selected.size === 1) {
              const id = Array.from(selected)[0];
              skipOne(id, reason, note);
              setShowSkipModal(false);
              clearSelection();
            } else {
              doBulkSkip(reason, note);
            }
          }} />
      )}
      {showBulkConfirm && (
        <BulkPushConfirm
          breakdown={bulkBreakdown}
          total={selected.size}
          onCancel={() => setShowBulkConfirm(false)}
          onConfirm={doBulkPush}
          busy={bulkBusy} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Counter tile
// ─────────────────────────────────────────────────────────────────────────
function CounterTile({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded p-2.5" style={{ background: '#F7F5F2',
                                              border: '1px solid #E5E1DC' }}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider"
           style={{ color: '#9CA3AF' }}>
        <Icon size={10} /> {label}
      </div>
      <div className="text-[20px] font-semibold mt-0.5" style={{ color }}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Empty-state component — explains WHY a filter has zero drafts.
// Each MCE trigger group has a different reason for being empty:
//   • Birthdays — only fires when DOB is in next 7 days
//   • Dormant   — 30-day cooldown after any prior MCE outreach (drained pool)
//   • Soul      — needs new members <48h, soul invite age 14d/30d, or
//                 power-users without Soul Form (rare on JLR — 99.9% complete)
//   • Curated   — monthly_curated only on day-1; quarterly_review only on
//                 last Sunday of Mar/Jun/Sep/Dec
// Showing this context turns "blank screen" into "ah, makes sense".
// ─────────────────────────────────────────────────────────────────────────
function nextMonthFirst(today) {
  const t = today ? new Date(today) : new Date();
  const next = new Date(t.getFullYear(), t.getMonth() + 1, 1);
  return next.toLocaleDateString('en-IN',
    { day: 'numeric', month: 'short', year: 'numeric' });
}

function nextQuarterEndSunday(today) {
  const t = today ? new Date(today) : new Date();
  // Find the next month from {Mar(2), Jun(5), Sep(8), Dec(11)} >= today's month
  const ends = [2, 5, 8, 11];
  const m = t.getMonth();
  const targetMonth = ends.find(e => e >= m) ?? ends[0];
  const yr = (targetMonth >= m) ? t.getFullYear() : t.getFullYear() + 1;
  // Last day of that month
  const last = new Date(yr, targetMonth + 1, 0);
  // Walk back to Sunday
  while (last.getDay() !== 0) last.setDate(last.getDate() - 1);
  return last.toLocaleDateString('en-IN',
    { day: 'numeric', month: 'short', year: 'numeric' });
}

const EMPTY_REASONS = {
  all: {
    title: (view) => view === 'today'
      ? 'No drafts to review today'
      : 'No drafts on this date',
    sub: (view) => view === 'today'
      ? 'The 03:00 IST cron generates prep briefs (T-7 internal). The 08:00 IST cron generates greetings (T-0 member-facing). CEO can also click "Generate today\'s drafts" to run the sweep now.'
      : 'Pick another date to see what was reviewed then.',
  },
  greeting: {
    title: () => 'No member greetings due today',
    sub: () => 'Member-facing greetings fire at 08:00 IST on the actual birthday / anniversary / festival day. If nobody on the roster has one of those today, this section is empty — that\'s correct. Tomorrow\'s 08:00 cron will pick up tomorrow\'s greetings.',
  },
  prep: {
    title: () => 'No prep briefs in the queue',
    sub: () => 'Prep briefs land at 03:00 IST when a member\'s birthday / anniversary is 1–7 days away. They\'re internal LSM tasks — never pushed to Zoho. If the queue is empty, every annual date in the next 7 days has already been briefed.',
  },
  birthday: {
    // legacy alias
    title: () => 'No birthdays in the next 7 days',
    sub: () => 'This trigger fires when a member, spouse, child birthday or wedding anniversary falls in the next-7-day window. Today\'s sweep found none for the selected client.',
  },
  dormant: {
    title: () => 'No dormant outreach today — pool is in cooldown',
    sub: () => 'Per Dipali doctrine, every member sits in a 30-day cooldown after any MCE note. Members who became dormant in earlier sweeps already received their outreach and are waiting out the cooldown. The pool replenishes as new members cross the 60d / 90d silence thresholds.',
  },
  soul: {
    title: () => 'No Soul Form follow-ups firing today',
    sub: () => 'Soul triggers only fire when: a new member was added in the last 48h, a Soul invite hits exactly 14d / 30d old (tight 24h windows), or a power-user has 3+ tickets without a Soul Form. JLR\'s Soul Form completion is 99.9%, so power-user nudges are rare.',
  },
  curated: {
    title: (view, today) => `Curated notes don\'t fire today`,
    sub: (view, today) => `Monthly curated notes fire on the 1st of each month — next: ${nextMonthFirst(today)}. Quarterly reviews fire on the last Sunday of Mar/Jun/Sep/Dec — next: ${nextQuarterEndSunday(today)}.`,
  },
};

function EmptyStateForFilter({ view, groupFilter, hasAnyData, today }) {
  const reason = EMPTY_REASONS[groupFilter] || EMPTY_REASONS.all;
  // Special case: if user is on "All" filter AND there's data in OTHER
  // groups (just none after filtering), nudge them to switch filter.
  const subtitle = (groupFilter !== 'all' && hasAnyData)
    ? reason.sub(view, today) + ' Other groups have drafts — try the "All" filter.'
    : reason.sub(view, today);

  return (
    <div className="text-center py-12 px-6 max-w-2xl mx-auto"
         data-testid="workbench-empty">
      <Sparkles size={28} className="mx-auto"
                 style={{ color: '#E5C9A0' }} />
      <div className="text-[14px] font-medium mt-2"
           style={{ color: '#1B2A4A' }}>
        {reason.title(view, today)}
      </div>
      <div className="text-[11.5px] mt-1.5" style={{ color: '#6B7280', lineHeight: 1.55 }}>
        {subtitle}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Section card (one trigger group)
// ─────────────────────────────────────────────────────────────────────────
function SectionCard({ section, selected, onToggle, onSelectAll,
                        onDeselectAll, onPreview, onPushOne, onMarkDone,
                        onSkipOne, onSuggest, readonly, canWrite }) {
  const [collapsed, setCollapsed] = useState(false);
  const Icon = ICONS[section.icon] || MessageCircle;
  const allSelected = section.drafts
    .filter(d => d.status === 'pending_review')
    .every(d => selected.has(d.draft_id));
  const anySelected = section.drafts
    .some(d => selected.has(d.draft_id));
  // Two-step doctrine (06-Feb-2026):
  // Prep group → navy badge "INTERNAL · for LSM action"
  // Greeting group → gold badge "MEMBER-FACING · push today"
  const isPrepSection = section.group === 'prep';
  const isGreetingSection = section.group === 'greeting';

  return (
    <div className="rounded-lg overflow-hidden"
         style={{ background: '#FFFEFB',
                   border: isPrepSection ? '1px solid #1B2A4A'
                                          : '1px solid #E5E1DC' }}
         data-testid={`workbench-section-${section.trigger}`}>
      <div className="px-4 py-2.5 flex items-center justify-between cursor-pointer"
           style={{ background: '#F7F5F2', borderBottom: '1px solid #E5E1DC' }}
           onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center gap-2">
          {collapsed ? <ChevronRight size={14} style={{ color: '#6B7280' }} />
                      : <ChevronDown size={14} style={{ color: '#6B7280' }} />}
          <Icon size={14} style={{ color: '#1B2A4A' }} />
          <span className="text-[13px] font-medium" style={{ color: '#1B2A4A' }}>
            {section.label}
          </span>
          <span className="text-[10.5px] px-1.5 py-0.5 rounded"
                style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
            {section.count}
          </span>
          {isPrepSection && (
            <span className="text-[9.5px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium"
                  style={{ background: '#1B2A4A', color: 'white' }}
                  title="Prep briefs are internal LSM tasks — never pushed to Zoho">
              Internal · LSM
            </span>
          )}
          {isGreetingSection && (
            <span className="text-[9.5px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium"
                  style={{ background: '#E5C9A0', color: '#1B2A4A' }}
                  title="Member-facing greeting — push to Zoho today">
              Member-facing
            </span>
          )}
          {section.pending_count > 0 && section.pending_count !== section.count && (
            <span className="text-[10.5px]" style={{ color: '#6B7280' }}>
              ({section.pending_count} pending)
            </span>
          )}
        </div>
        {!readonly && canWrite && section.pending_count > 0 && (
          <button onClick={(e) => {
                    e.stopPropagation();
                    if (allSelected) onDeselectAll(); else onSelectAll();
                  }}
                  data-testid={`workbench-section-select-${section.trigger}`}
                  className="text-[10.5px] px-2 py-0.5 rounded hover:bg-white"
                  style={{ color: '#1B2A4A',
                            border: '1px solid #E5E1DC' }}>
            {allSelected ? 'Deselect all' : `Select all ${section.pending_count}`}
          </button>
        )}
      </div>
      {!collapsed && (
        <div>
          {section.drafts.map(draft => (
            <DraftRow key={draft.draft_id} draft={draft}
                       selected={selected.has(draft.draft_id)}
                       onToggle={() => onToggle(draft.draft_id)}
                       onPreview={() => onPreview(draft)}
                       onPushOne={() => onPushOne(draft.draft_id)}
                       onMarkDone={() => onMarkDone(draft.draft_id)}
                       onSkipOne={() => onSkipOne(draft)}
                       onSuggest={() => onSuggest(draft.draft_id)}
                       readonly={readonly} canWrite={canWrite} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Draft row (one member card)
// ─────────────────────────────────────────────────────────────────────────
function DraftRow({ draft, selected, onToggle, onPreview, onPushOne,
                     onMarkDone, onSkipOne, onSuggest, readonly, canWrite }) {
  const status = draft.status;
  const isPending = status === 'pending_review';
  const isPushed = status === 'pushed';
  const isSkipped = status === 'skipped';
  const isConverted = status === 'converted';
  const isDone = status === 'done';
  const isDormant = (draft.trigger || '').startsWith('dormant');
  const [suggestBusy, setSuggestBusy] = useState(false);
  const handleSuggest = async () => {
    setSuggestBusy(true);
    try { await onSuggest(); } finally { setSuggestBusy(false); }
  };

  return (
    <div className="px-4 py-3 flex items-start gap-3 border-b last:border-b-0"
         style={{ borderColor: '#F0EEE9' }}
         data-testid={`workbench-draft-${draft.draft_id}`}>
      {!readonly && canWrite && isPending && (
        <input type="checkbox" checked={selected} onChange={onToggle}
                data-testid={`workbench-checkbox-${draft.draft_id}`}
                className="mt-1 cursor-pointer" />
      )}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium" style={{ color: '#1B2A4A' }}>
            {draft.member_name || '—'}
          </span>
          {draft.member_tier && (
            <span className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: '#1B2A4A', color: 'white' }}>
              {draft.member_tier}
            </span>
          )}
          {draft.member_city && (
            <span className="text-[11px]" style={{ color: '#6B7280' }}>
              · {draft.member_city}
            </span>
          )}
          {draft.lsm && (
            <span className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: '#FFF8EE', color: '#8A4B00' }}>
              LSM: {draft.lsm}
            </span>
          )}
          {isPushed && (
            <span className="text-[10px] px-1.5 py-0.5 rounded ml-auto"
                  style={{ background: '#ECFDF5', color: '#1f5736' }}>
              ✓ Sent to Zoho
            </span>
          )}
          {isConverted && (
            <span className="text-[10px] px-1.5 py-0.5 rounded ml-auto"
                  title="Prep brief was converted to a member-facing greeting and pushed to Zoho"
                  style={{ background: '#FEF3E7', color: '#8A4B00' }}>
              ✓ Converted to greeting
            </span>
          )}
          {isDone && (
            <span className="text-[10px] px-1.5 py-0.5 rounded ml-auto"
                  title="Prep brief marked as actioned by the LSM"
                  style={{ background: '#EEF2FF', color: '#1B2A4A' }}>
              ✓ Marked done
            </span>
          )}
          {isSkipped && (
            <span className="text-[10px] px-1.5 py-0.5 rounded ml-auto"
                  style={{ background: '#F3F4F6', color: '#6B7280' }}>
              Skipped · {draft.skip_reason}
            </span>
          )}
        </div>
        {(draft.soul_chips || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {draft.soul_chips.slice(0, 4).map((c, i) => (
              <span key={i}
                    className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{ background: '#F7F5F2', color: '#1B2A4A',
                              border: '1px solid #E5E1DC' }}>
                {c}
              </span>
            ))}
          </div>
        )}
        <div className="text-[12.5px] line-clamp-2"
             style={{ color: '#1B2A4A',
                       fontFamily: 'Georgia, "Times New Roman", serif',
                       lineHeight: 1.55 }}>
          <span style={{ color: '#9CA3AF' }}>"</span>
          {(draft.body || '').slice(0, 220)}
          {(draft.body || '').length > 220 && '…'}
          <span style={{ color: '#9CA3AF' }}>"</span>
        </div>
        {(draft.talking_points || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {draft.talking_points.map((p, i) => (
              <span key={i}
                    className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{ background: '#FFF4E3', color: '#8A4B00' }}>
                {p}
              </span>
            ))}
          </div>
        )}
        {/* Suggested next experience — only for dormant triggers
           (the others have specific time-anchored content already) */}
        {isDormant && draft.suggested_next_experience && (
          <div className="text-[12px] mt-1.5 px-2.5 py-2 rounded"
               style={{ background: '#ECF2FA',
                         border: '1px solid #B7C2D8',
                         color: '#1B2A4A',
                         fontFamily: 'Georgia, "Times New Roman", serif',
                         lineHeight: 1.5 }}
               data-testid={`workbench-suggestion-${draft.draft_id}`}>
            <div className="text-[9.5px] uppercase tracking-wider font-medium mb-0.5"
                 style={{ color: '#3A4A6A',
                           fontFamily: 'Inter, system-ui, sans-serif' }}>
              ✨ Suggested anchor
            </div>
            {draft.suggested_next_experience}
          </div>
        )}
      </div>
      {/* Action buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {isDormant && isPending && !readonly && canWrite
          && !draft.suggested_next_experience && (
          <button onClick={handleSuggest}
                  disabled={suggestBusy}
                  title="Ask Claude to suggest a specific outreach anchor for this dormant member"
                  data-testid={`workbench-suggest-${draft.draft_id}`}
                  className="text-[10px] px-1.5 py-1 rounded inline-flex items-center gap-1 hover:bg-gray-50 disabled:opacity-60"
                  style={{ color: '#1B2A4A',
                            border: '1px solid #B7C2D8',
                            background: '#ECF2FA' }}>
            {suggestBusy
              ? <Loader2 size={10} className="animate-spin" />
              : <Sparkles size={10} />}
            {suggestBusy ? 'Thinking…' : 'Suggest'}
          </button>
        )}
        <button onClick={onPreview}
                title="Preview / edit"
                data-testid={`workbench-preview-${draft.draft_id}`}
                className="text-[10px] px-1.5 py-1 rounded hover:bg-gray-50 inline-flex items-center gap-1"
                style={{ color: '#1B2A4A',
                          border: '1px solid #E5E1DC' }}>
          <Eye size={10} /> Preview
        </button>
        {!readonly && canWrite && isPending && (
          <>
            {draft.is_prep_brief ? (
              <button onClick={onMarkDone}
                      title="Mark this prep brief as actioned — the member-facing greeting fires automatically at 08:00 IST on the actual day"
                      data-testid={`workbench-markdone-${draft.draft_id}`}
                      className="text-[10px] px-1.5 py-1 rounded text-white hover:opacity-90 inline-flex items-center gap-1"
                      style={{ background: '#1B2A4A' }}>
                <CheckCircle2 size={10} /> Mark Done
              </button>
            ) : (
              <button onClick={onPushOne}
                      title="Push to Zoho"
                      data-testid={`workbench-push-${draft.draft_id}`}
                      className="text-[10px] px-1.5 py-1 rounded text-white hover:opacity-90 inline-flex items-center gap-1"
                      style={{ background: '#1B2A4A' }}>
                <Send size={10} /> Send
              </button>
            )}
            <button onClick={onSkipOne}
                    title="Skip"
                    data-testid={`workbench-skip-${draft.draft_id}`}
                    className="text-[10px] px-1.5 py-1 rounded hover:bg-gray-50"
                    style={{ color: '#6B7280',
                              border: '1px solid #E5E1DC' }}>
              <X size={10} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
